<template>
  <div class="space-y-6">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <!-- Left Column: Name and Description -->
      <div class="space-y-6">
        <div class="space-y-2">
          <label class="text-[14px] font-bold text-foreground"
            >Project Name*</label
          >
          <input
            :value="item.project_name"
            @input="update('project_name', $event.target.value)"
            type="text"
            placeholder="Project name"
            class="w-full px-5 py-3 rounded-xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all"
            required
          />
        </div>

        <div class="space-y-2">
          <label class="text-[14px] font-bold text-foreground"
            >Project URL</label
          >
          <input
            :value="item.project_url"
            @input="update('project_url', $event.target.value)"
            type="url"
            placeholder="https://..."
            class="w-full px-5 py-3 rounded-xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="text-[14px] font-bold text-foreground"
              >Start Month*</label
            >
            <select
              :value="item.start_month"
              @change="update('start_month', $event.target.value)"
              class="w-full px-5 py-3 rounded-xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all appearance-none"
              required
            >
              <option value="" disabled>Month</option>
              <option v-for="month in months" :key="month" :value="month">
                {{ month }}
              </option>
            </select>
          </div>
          <div class="space-y-2">
            <label class="text-[14px] font-bold text-foreground"
              >Start Year*</label
            >
            <select
              :value="item.start_year"
              @change="update('start_year', parseInt($event.target.value))"
              class="w-full px-5 py-3 rounded-xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all appearance-none"
              required
            >
              <option value="" disabled>Year</option>
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="text-[14px] font-bold text-foreground"
              >End Month</label
            >
            <select
              :value="item.end_month"
              @change="update('end_month', $event.target.value)"
              class="w-full px-5 py-3 rounded-xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all appearance-none"
            >
              <option value="">Month</option>
              <option v-for="month in months" :key="month" :value="month">
                {{ month }}
              </option>
            </select>
          </div>
          <div class="space-y-2">
            <label class="text-[14px] font-bold text-foreground"
              >End Year</label
            >
            <select
              :value="item.end_year"
              @change="update('end_year', parseInt($event.target.value))"
              class="w-full px-5 py-3 rounded-xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all appearance-none"
            >
              <option value="">Year</option>
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Right Column: Image and Description -->
      <div class="space-y-6">
        <div class="space-y-2">
          <label class="text-[14px] font-bold text-foreground"
            >Project image</label
          >
          <div
            class="relative group cursor-pointer border-2 border-dashed border-border/60 hover:border-primary/40 rounded-2xl bg-muted/10 transition-all p-8 flex flex-col items-center justify-center gap-3 min-h-[160px] overflow-hidden"
          >
            <img
              v-if="previewUrl || item.project_image"
              :src="previewUrl || item.project_image"
              class="absolute inset-0 w-full h-full object-cover"
            />
            <div
              v-if="!previewUrl && !item.project_image"
              class="flex flex-col items-center justify-center gap-3"
            >
              <div
                class="w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center shadow-sm"
              >
                <Upload class="w-5 h-5 text-muted-foreground" />
              </div>
              <div class="text-center space-y-1">
                <p class="text-sm font-medium">
                  Drag image here or
                  <span class="text-primary hover:underline">browse</span> your
                  files.
                </p>
                <p class="text-[11px] text-muted-foreground/60 font-medium">
                  Recommended image size: 800 x 600 px
                </p>
              </div>
            </div>
            <input
              type="file"
              @change="onFileSelected"
              class="absolute inset-0 opacity-0 cursor-pointer z-10"
              accept="image/*"
            />

            <!-- Compression Loading Overlay -->
            <div
              v-if="isCompressing"
              class="absolute inset-0 bg-background/80 flex flex-col items-center justify-center gap-2 z-20"
            >
              <div
                class="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"
              ></div>
              <span class="text-[10px] font-medium text-muted-foreground"
                >Optimizing...</span
              >
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <div class="flex justify-between items-center px-1">
            <label class="text-[14px] font-bold text-foreground"
              >Description*</label
            >
            <span class="text-xs text-muted-foreground font-medium">
              {{ item.description ? item.description.length : 0 }}/1000
            </span>
          </div>
          <div class="relative group">
            <textarea
              :value="item.description"
              @input="update('description', $event.target.value)"
              maxlength="1000"
              placeholder="It is important for products..."
              class="block w-full h-[152px] px-6 py-5 rounded-2xl border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 transition-all resize-none leading-relaxed text-[15px]"
              required
            ></textarea>
            <svg
              class="absolute bottom-4 right-4 w-4 h-4 text-muted-foreground/30"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 15L15 21M21 8L8 21"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Upload } from "lucide-vue-next";
import imageCompression from "browser-image-compression";

export default {
  name: "ProjectForm",
  components: {
    Upload,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      previewUrl: null,
      isCompressing: false,
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      years: Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i),
    };
  },
  methods: {
    update(key, value) {
      this.$emit("update", { [key]: value });
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isCompressing = true;
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);

        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
        }
        this.previewUrl = URL.createObjectURL(compressedFile);
        this.update("project_image", compressedFile);
      } catch (error) {
        console.error("Compression Error:", error);
      } finally {
        this.isCompressing = false;
      }
    },
  },
  beforeUnmount() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>
